import React from 'react';
import { Link } from 'react-router-dom';
import { Language } from '../translations';
import { Expedition, Category } from '../types';
import { getLocalizedPath, formatDuration } from '../lib/i18n';

interface ExpeditionsProps {
    t: any;
    lang: Language;
    filter: Category;
    setFilter: (c: Category) => void;
    expeditions: Expedition[];
    getExpField: <T>(exp: any, field: string, lang: Language) => T;
}

const ExpeditionCard: React.FC<{
    exp: Expedition;
    lang: Language;
    t: any;
    getExpField: <T>(exp: any, field: string, lang: Language) => T;
}> = ({ exp, lang, t, getExpField }) => {
    const [currentImageIndex, setCurrentImageIndex] = React.useState(-1); // -1 means showing main cover
    const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    // Filter out the cover image from the gallery to avoid repetition
    const previewGallery = React.useMemo(() => {
        if (!exp.gallery) return [];
        return exp.gallery.filter(img => {
            const src = typeof img === 'string' ? img : img.src;
            return src !== exp.image;
        });
    }, [exp.gallery, exp.image]);

    const handleMouseEnter = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);

        hoverTimeoutRef.current = setTimeout(() => {
            if (previewGallery.length > 0) {
                setCurrentImageIndex(0);
                intervalRef.current = setInterval(() => {
                    setCurrentImageIndex(prev => (prev + 1) % previewGallery.length);
                }, 3000);
            }
        }, 2000);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        setCurrentImageIndex(-1);
    };

    const totalSlides = previewGallery.length + 1;

    return (
        <Link
            to={getLocalizedPath(`/expeditions/${exp.slug}`, lang)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="bg-stone border border-rock overflow-hidden hover:border-[#00d4ff] transition-all group cursor-pointer block"
        >
            <div className="h-64 overflow-hidden relative">
                {/* SLIDING CONTAINER */}
                <div
                    className="flex h-full transition-transform duration-700 ease-in-out"
                    style={{
                        width: `${totalSlides * 100}%`,
                        transform: `translateX(-${(currentImageIndex + 1) * (100 / totalSlides)}%)`
                    }}
                >
                    {/* Main Cover */}
                    <div
                        className="h-full flex-shrink-0"
                        style={{ width: `${100 / totalSlides}%` }}
                    >
                        <img
                            src={exp.image}
                            alt={getExpField<string>(exp, 'name', lang)}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    {/* Gallery Images (filtered) */}
                    {previewGallery.map((img, idx) => (
                        <div
                            key={idx}
                            className="h-full flex-shrink-0"
                            style={{ width: `${100 / totalSlides}%` }}
                        >
                            <img
                                src={typeof img === 'string' ? img : img.src}
                                alt={getExpField<string>(exp, 'name', lang)}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ))}
                </div>

                <div className="absolute top-3 left-3 flex gap-2 z-10">
                    <span className="bg-black/80 px-2 py-0.5 text-[9px] uppercase font-bold tracking-widest">{t.difficulty}: {t[`diff_${exp.difficulty}`]}</span>
                </div>

                {/* Indicator dots for the preview */}
                {currentImageIndex !== -1 && (
                    <div className="absolute bottom-2 right-2 flex gap-1 bg-black/40 px-2 py-1 rounded-full backdrop-blur-sm z-10">
                        {/* Dot for cover */}
                        <div className={`w-1 h-1 rounded-full transition-all ${currentImageIndex === -1 ? 'bg-[#00d4ff] scale-125' : 'bg-white/30'}`} />
                        {/* Dots for gallery items */}
                        {previewGallery.map((_, idx) => (
                            <div
                                key={idx}
                                className={`w-1 h-1 rounded-full transition-all ${idx === currentImageIndex ? 'bg-[#00d4ff] scale-125' : 'bg-white/30'}`}
                            />
                        ))}
                    </div>
                )}
            </div>
            <div className="p-6">
                <h3 className="text-3xl mb-2 leading-none uppercase">{getExpField<string>(exp, 'name', lang)}</h3>
                <p className="text-gray-500 text-xs mb-4 line-clamp-2 italic font-light h-8 leading-tight">{getExpField<string>(exp, 'desc', lang)}</p>
                <div className="flex justify-between items-end border-t border-rock pt-4">
                    <div>
                        <p className="text-[9px] uppercase tracking-widest text-gray-600 mb-0.5">{t.duration}</p>
                        <p className="text-sm font-bold tracking-tight">{formatDuration(exp.duration, lang)}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[9px] uppercase tracking-widest text-gray-600 mb-0.5">{t.from}</p>
                        <p className="text-2xl font-title text-[#00d4ff] leading-none">
                            {exp.price_from !== null ? `USD$${exp.price_from}` : t.consult}
                        </p>
                    </div>
                </div>
            </div>
        </Link>
    );
};

const Expeditions: React.FC<ExpeditionsProps> = ({ t, lang, filter, setFilter, expeditions, getExpField }) => {
    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 pt-4 pb-12 sm:py-16 px-4 sm:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-8 sm:mb-16 space-y-2 sm:space-y-4">
                <h1 className="text-3xl sm:text-7xl font-title uppercase tracking-tighter">
                    {filter === 'wellness' ? t.cat_wellness : t.exp_title}
                </h1>
                <p className="text-gray-500 uppercase tracking-widest text-[11px] sm:text-xs font-bold">
                    {filter === 'wellness' ? (lang === 'es' ? 'Nuevas formas de conectar con el Valle' : 'New ways to connect with the Valley') : t.exp_subtitle}
                </p>
                {filter === 'wellness' && (
                    <div className="flex flex-wrap justify-center gap-2 mt-4 sm:mt-6 max-w-2xl mx-auto">
                        {['Yoga', 'Tinajas', 'Masajes', 'Cabalgatas', 'Asado'].map(opt => (
                            <span key={opt} className="px-3 py-1 bg-stone border border-rock text-[9px] text-[#ec4899] font-bold uppercase tracking-widest">
                                {opt}
                            </span>
                        ))}
                    </div>
                )}
            </div>


            {filter !== 'wellness' && (
                <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-16">
                    {(['all', 'trekking', 'climbing', 'water'] as Category[]).map(cat => (
                        <button
                            key={cat}
                            onClick={() => setFilter(cat)}
                            className={`px-3.5 sm:px-6 py-1.5 border border-rock text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all ${filter === cat ? 'bg-[#00d4ff] border-[#00d4ff] text-black' : 'hover:border-white'}`}
                        >
                            {t[`cat_${cat}`]}
                        </button>
                    ))}
                </div>
            )}


            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {expeditions.length === 0 ? (
                    Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-stone border border-rock overflow-hidden animate-pulse">
                            <div className="h-64 bg-black/40" />
                            <div className="p-6 space-y-4">
                                <div className="h-6 bg-white/10 rounded w-3/4" />
                                <div className="h-4 bg-white/5 rounded w-full" />
                                <div className="h-4 bg-white/5 rounded w-1/2" />
                                <div className="flex justify-between items-end border-t border-rock pt-4">
                                    <div className="h-4 bg-white/10 rounded w-20" />
                                    <div className="h-6 bg-white/10 rounded w-16" />
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    expeditions.map(exp => (
                        <ExpeditionCard
                            key={exp.id}
                            exp={exp}
                            lang={lang}
                            t={t}
                            getExpField={getExpField}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default Expeditions;
